import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, type ReactNode } from 'react';
import { matchPath } from 'react-router-dom';
import useSegmentTracking from './useSegmentTracking';

export const FEATURE_LOAD_DURATION_EVENT = 'Feature - Load Duration';

export type FeatureSet = 'disease-risk' | 'risk-management' | 'regional-overview';
export type FeatureLoadRequestType = 'disease-risk-by-property' | 'regional-overview-results';
export type FeatureLoadOutcome = 'success' | 'error';
export type FeatureLoadSource = 'network' | 'cache' | 'mock';

export interface FeatureLoadRoute {
  activationKey: string;
  featureSet: FeatureSet;
  model: string;
  propertyId?: string;
}

export interface FeatureLoadCompletion {
  loadSource: FeatureLoadSource;
  outcome: FeatureLoadOutcome;
  requestType: FeatureLoadRequestType;
}

interface FeatureLoadSession extends FeatureLoadRoute {
  active: boolean;
  completed: boolean;
  completion?: FeatureLoadCompletion;
  ready: boolean;
  startedAt: number;
}

interface FeatureLoadIdentity {
  featureSet?: FeatureSet;
  propertyId?: string;
  regionalModel?: string;
}

interface FeatureLoadTimingProviderProps {
  children: ReactNode;
  pathname: string;
}

type CompleteFeatureLoad = (completion: FeatureLoadCompletion, identity?: FeatureLoadIdentity) => void;
type FinishFeatureLoad = (identity?: FeatureLoadIdentity) => void;

interface FeatureLoadTimingContextValue {
  completeFeatureLoad: CompleteFeatureLoad;
  finishFeatureLoad: FinishFeatureLoad;
  recordFeatureLoadCompletion: CompleteFeatureLoad;
}

const noopFeatureLoadTimingAction = () => undefined;

const FeatureLoadTimingContext = createContext<FeatureLoadTimingContextValue>({
  completeFeatureLoad: noopFeatureLoadTimingAction,
  finishFeatureLoad: noopFeatureLoadTimingAction,
  recordFeatureLoadCompletion: noopFeatureLoadTimingAction
});

const matchesSession = (session: FeatureLoadSession, identity?: FeatureLoadIdentity): boolean =>
  (!identity?.featureSet || identity.featureSet === session.featureSet) &&
  (!identity?.propertyId || identity.propertyId === session.propertyId) &&
  (!identity?.regionalModel || (session.featureSet !== 'disease-risk' && identity.regionalModel === session.model));

export const getFeatureLoadRoute = (pathname: string): FeatureLoadRoute | undefined => {
  const diseaseRiskMatch = matchPath({ path: '/company/:companyId/property/:propertyId/disease-risk', end: true }, pathname);

  if (diseaseRiskMatch) {
    const { companyId, propertyId } = diseaseRiskMatch.params;

    if (companyId && propertyId) {
      return {
        activationKey: `disease-risk:${companyId}:${propertyId}`,
        featureSet: 'disease-risk',
        model: 'disease-risk',
        propertyId
      };
    }
  }

  const riskManagementModelMatch = matchPath({ path: '/risk-management/:companyId/:model/*', end: false }, pathname);
  const riskManagementMatch = riskManagementModelMatch ?? matchPath({ path: '/risk-management/:companyId', end: false }, pathname);

  if (riskManagementMatch?.params.companyId) {
    const model = riskManagementModelMatch?.params.model;

    return {
      activationKey: `risk-management:${riskManagementMatch.params.companyId}:${model && model !== 'disease-risk' ? model : 'default'}`,
      featureSet: 'risk-management',
      model: model ?? 'disease-risk'
    };
  }

  const regionalOverviewModelMatch = matchPath({ path: '/company/:companyId/regional-overview/:model/*', end: false }, pathname);
  const regionalOverviewMatch =
    regionalOverviewModelMatch ?? matchPath({ path: '/company/:companyId/regional-overview', end: false }, pathname);

  if (regionalOverviewMatch?.params.companyId) {
    const model = regionalOverviewModelMatch?.params.model;

    return {
      activationKey: `regional-overview:${regionalOverviewMatch.params.companyId}:${model && model !== 'disease-risk' ? model : 'default'}`,
      featureSet: 'regional-overview',
      model: model ?? 'disease-risk'
    };
  }

  return undefined;
};

export const FeatureLoadTimingProvider = ({ children, pathname }: FeatureLoadTimingProviderProps) => {
  const featureRoute = useMemo(() => getFeatureLoadRoute(pathname), [pathname]);
  const { track } = useSegmentTracking(false, false, false);
  const trackRef = useRef(track);
  const featureSet = featureRoute?.featureSet;
  const activationKey = featureRoute?.activationKey;
  const model = featureRoute?.model;
  const propertyId = featureRoute?.propertyId;

  const session = useMemo<FeatureLoadSession | undefined>(() => {
    if (!featureSet || !activationKey || !model) {
      return undefined;
    }

    return {
      activationKey,
      active: false,
      completed: false,
      featureSet,
      model,
      propertyId,
      ready: false,
      startedAt: 0
    };
  }, [activationKey, featureSet, model, propertyId]);

  useLayoutEffect(() => {
    trackRef.current = track;
  }, [track]);

  useLayoutEffect(() => {
    if (!session) {
      return;
    }

    session.active = true;
    session.startedAt = performance.now();

    return () => {
      session.active = false;
    };
  }, [session]);

  const completeFeatureLoad = useCallback<CompleteFeatureLoad>(
    (completion, identity) => {
      if (!session?.active || session.completed || !matchesSession(session, identity)) {
        return;
      }

      session.completed = true;

      const featureLoadDetails = {
        durationMs: Math.max(0, Math.round(performance.now() - session.startedAt)),
        featureModel: session.model,
        featureSet: session.featureSet,
        loadSource: completion.loadSource,
        outcome: completion.outcome,
        requestType: completion.requestType
      };

      trackRef.current(FEATURE_LOAD_DURATION_EVENT, featureLoadDetails);
    },
    [session]
  );

  const recordFeatureLoadCompletion = useCallback<CompleteFeatureLoad>(
    (completion, identity) => {
      if (!session?.active || session.completed || session.completion || !matchesSession(session, identity)) {
        return;
      }

      session.completion = completion;

      if (session.ready) {
        completeFeatureLoad(completion, identity);
      }
    },
    [completeFeatureLoad, session]
  );

  const finishFeatureLoad = useCallback<FinishFeatureLoad>(
    identity => {
      if (!session?.active || session.completed || !matchesSession(session, identity)) {
        return;
      }

      session.ready = true;

      if (session.completion) {
        completeFeatureLoad(session.completion, identity);
      }
    },
    [completeFeatureLoad, session]
  );

  const featureLoadTimingContextValue = useMemo(
    () => ({ completeFeatureLoad, finishFeatureLoad, recordFeatureLoadCompletion }),
    [completeFeatureLoad, finishFeatureLoad, recordFeatureLoadCompletion]
  );

  return <FeatureLoadTimingContext.Provider value={featureLoadTimingContextValue}>{children}</FeatureLoadTimingContext.Provider>;
};

export const useFeatureLoadTiming = (): CompleteFeatureLoad => useContext(FeatureLoadTimingContext).completeFeatureLoad;

export const useFeatureLoadTimingLifecycle = (): FeatureLoadTimingContextValue => useContext(FeatureLoadTimingContext);

export const useFeatureLoadReady = (isReady: boolean, { featureSet, regionalModel }: FeatureLoadIdentity = {}): void => {
  const { finishFeatureLoad } = useFeatureLoadTimingLifecycle();

  useEffect(() => {
    if (isReady) {
      finishFeatureLoad({ featureSet, regionalModel });
    }
  }, [featureSet, finishFeatureLoad, isReady, regionalModel]);
};
